import { useKeycloak } from "@react-keycloak/web";
import { useEffect } from "react";
import { create } from "zustand";
import { getUserProfile } from "../api/users";

const CACHE_KEY = 'finderz:currentUserProfile';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min - shorter than the backend's 1h Redis TTL

interface CachedProfile {
  profile: UserProfile;
  userId: string;
  fetchedAt: number;
}

function readCache(userId: string): UserProfile | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry: CachedProfile = JSON.parse(raw);
    if (entry.userId !== userId || Date.now() - entry.fetchedAt > CACHE_TTL_MS) return null;
    return entry.profile;
  } catch {
    return null;
  }
}

function writeCache(userId: string, profile: UserProfile) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ profile, userId, fetchedAt: Date.now() }));
  } catch {
    // sessionStorage unavailable (private mode, quota, etc) - caching is best-effort
  }
}

interface CurrentUserState {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  fetchedForUserId: string | null;
  fetchCurrentUser: (token: string, userId: string) => Promise<void>;
  setProfile: (profile: UserProfile) => void;
  clear: () => void;
}

export const useCurrentUserStore = create<CurrentUserState>((set, get) => ({
  profile: null,
  isLoading: false,
  error: null,
  fetchedForUserId: null,
  fetchCurrentUser: async (token, userId) => {
    if (!userId) return;
    if (get().fetchedForUserId === userId && (get().profile || get().isLoading)) return;

    const cached = readCache(userId);
    if (cached) {
      set({ profile: cached, fetchedForUserId: userId, isLoading: false, error: null });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const profile = await getUserProfile(token, userId);
      set({ profile, isLoading: false, fetchedForUserId: userId });
      writeCache(userId, profile);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load profile';
      set({ error: message, isLoading: false, fetchedForUserId: userId });
    }
  },
  setProfile: (profile) => {
    const userId = get().fetchedForUserId;
    set({ profile });
    if (userId) writeCache(userId, profile);
  },
  clear: () => {
    set({ profile: null, fetchedForUserId: null, error: null });
    try {
      sessionStorage.removeItem(CACHE_KEY);
    } catch {
      // ignore
    }
  },
}));

export const useCurrentUser = () => {
  const { keycloak } = useKeycloak();
  const { profile, isLoading, error, fetchCurrentUser } = useCurrentUserStore();
  const userId = keycloak?.tokenParsed?.sub;

  useEffect(() => {
    if (keycloak?.token && userId) {
      fetchCurrentUser(keycloak.token, userId);
    }
  }, [keycloak?.token, userId]);

  return { profile, isLoading, error };
};
